import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/lib/notify";
import API from "@/lib/api";
import { ShieldCheck, ArrowLeft, Info, Building2 } from "lucide-react";
import { useIdempotentSubmit } from "@/hooks/useIdempotentSubmit";

const ACTIONS = [
  { key: "can_view", label: "View" },
  { key: "can_add", label: "Add" },
  { key: "can_edit", label: "Edit" },
  { key: "can_delete", label: "Delete" },
];

// Roles whose funds + permission matrix are managed automatically by GL.
const AUTO_ROLE_TAGS = ["org_admin", "gl_super_admin"];
const isAutoRole = (tag) => AUTO_ROLE_TAGS.includes((tag || "").toLowerCase());

const deriveTag = (name) =>
  (name || "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");

const normalizeList = (d) => (Array.isArray(d) ? d : d?.data || d?.modules || d?.roles || []);
const modKey = (m) => m.module_key || m.key || m.id || m.module_id;
const modLabel = (m) => m.module_name || m.name || m.label || m.title || modKey(m);

const STATUS_STYLES = {
  active: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  draft: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  closed: "bg-red-500/10 text-red-500 border-red-500/20",
};

export default function RoleForm() {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!roleId;
  const preselectedOrg = searchParams.get("org_id") || "";
  // TY-43/TY-44 fund picker state
  const [fundSearch, setFundSearch] = useState("");
  const [fundStatusFilter, setFundStatusFilter] = useState("active");
  const [fundsLoading, setFundsLoading] = useState(false);

  const [orgs, setOrgs] = useState([]);
  const [funds, setFunds] = useState([]);
  const [modules, setModules] = useState([]);
  const [form, setForm] = useState({ role_name: "", description: "", org_id: "", role_tag: "" });
  const [selectedFunds, setSelectedFunds] = useState([]);
  const [matrix, setMatrix] = useState({}); // { fund_id: { module_key: {can_view, can_add, can_edit, can_delete} } }
  const [loading, setLoading] = useState(true);
  const { submitting, run: runSubmit } = useIdempotentSubmit();
  // TY-46: inline validation state + refs used by scroll-to-first-error.
  const [errors, setErrors] = useState({ role_name: "", org_id: "", permissions: "" });
  const nameNodeRef = useRef(null);
  const orgAnchorNodeRef = useRef(null);
  const permsAnchorNodeRef = useRef(null);

  const roleTag = isEdit ? form.role_tag || deriveTag(form.role_name) : deriveTag(form.role_name);
  const auto = isAutoRole(roleTag);
  // TY-43: fund picker — status filter (default Active), search, closed-disabled.
  const orgFunds = useMemo(() => {
    const base = funds.filter((f) => !form.org_id || f.org_id === form.org_id);
    const filtered = base.filter((f) => {
      if (fundStatusFilter !== "all" && (f.status || "").toLowerCase() !== fundStatusFilter) return false;
      if (fundSearch.trim() && !(f.name || "").toLowerCase().includes(fundSearch.trim().toLowerCase())) return false;
      return true;
    });
    return filtered;
  }, [funds, form.org_id, fundStatusFilter, fundSearch]);

  // Initial load: orgs only (funds are fetched org-scoped, TY-44). On edit,
  // prefill the role and its currently-attached org's funds.
  useEffect(() => {
    (async () => {
      try {
        const orgRes = await API.get("/organizations");
        const orgList = orgRes.data || [];
        setOrgs(orgList);

        if (isEdit) {
          const { data } = await API.get(`/roles-permissions/roles/${roleId}`);
          const role = Array.isArray(data) ? data[0] : data?.data || data;
          const m = {};
          const fundsSet = new Set();
          (role?.permissions || []).forEach((p) => {
            if (!p.fund_id) return;
            fundsSet.add(p.fund_id);
            const k = p.module_key;
            if (!k) return;
            const fundM = m[p.fund_id] || {};
            fundM[k] = {
              can_view: !!p.can_view,
              can_add: !!p.can_add,
              can_edit: !!p.can_edit,
              can_delete: !!p.can_delete,
            };
            m[p.fund_id] = fundM;
          });
          let selFunds = Array.from(fundsSet);
          if (selFunds.length === 0 && Array.isArray(role?.funds)) {
            selFunds = role.funds.map((f) => f.fund_id || f.id || f).filter(Boolean);
          }
          setMatrix(m);
          setSelectedFunds(selFunds);
          setForm({
            // TY-40: prefer role_description on read; fall back for pre-migration rows.
            role_name: role?.role_name || role?.name || "",
            description: role?.role_description || role?.description || "",
            org_id: role?.org_id || "",
            role_tag: role?.role_tag || "",
          });
        } else if (preselectedOrg && orgList.some((o) => o.id === preselectedOrg)) {
          // TY-35: honor ?org_id=... coming from the list page.
          setForm((prev) => ({ ...prev, org_id: preselectedOrg }));
        } else if (orgList.length) {
          setForm((prev) => ({ ...prev, org_id: orgList[0].id }));
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load role data");
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, roleId, preselectedOrg]);

  // TY-44: org-scoped fund fetch — replaces the prior all-orgs full payload.
  // Only fetches the picker fields (id, name, status) for the selected org.
  useEffect(() => {
    if (!form.org_id) { setFunds([]); return; }
    let cancelled = false;
    setFundsLoading(true);
    API.get(`/funds?org_id=${encodeURIComponent(form.org_id)}`)
      .then(({ data }) => {
        if (cancelled) return;
        const list = (data || []).map((f) => ({ id: f.id, name: f.name, status: f.status, org_id: f.org_id }));
        setFunds(list);
      })
      .catch(() => { if (!cancelled) setFunds([]); })
      .finally(() => { if (!cancelled) setFundsLoading(false); });
    return () => { cancelled = true; };
  }, [form.org_id]);

  // Load the module list for the permission matrix whenever the org changes.
  const fetchModules = useCallback(async (orgId) => {
    try {
      const { data } = await API.get("/roles-permissions/modules", { params: orgId ? { org_id: orgId } : {} });
      setModules(normalizeList(data));
    } catch (err) {
      setModules([]);
      toast.error(err.response?.data?.detail || "Failed to load modules");
    }
  }, []);

  useEffect(() => {
    if (!loading && !auto) fetchModules(form.org_id);
  }, [form.org_id, loading, auto, fetchModules]);

  const toggleAction = (fundId, mk, action) =>
    setMatrix((prev) => {
      const cell = { ...(prev[fundId]?.[mk] || {}) };
      const nextVal = !cell[action];
      cell[action] = nextVal;
      // TY-42: enabling any of add/edit/delete auto-turns View on.
      if (["can_add", "can_edit", "can_delete"].includes(action) && nextVal) {
        cell.can_view = true;
      }
      // TY-42: block turning View off while any dependent perm is on.
      if (action === "can_view" && !nextVal && (cell.can_add || cell.can_edit || cell.can_delete)) {
        return prev;
      }
      return { ...prev, [fundId]: { ...(prev[fundId] || {}), [mk]: cell } };
    });

  const toggleFund = (fid) =>
    setSelectedFunds((prev) => (prev.includes(fid) ? prev.filter((x) => x !== fid) : [...prev, fid]));

  // "Select All" per fund — toggles a single action column across all modules.
  const isFundActionAllChecked = (fundId, action) =>
    modules.length > 0 && modules.every((m) => matrix[fundId]?.[modKey(m)]?.[action]);

  const toggleFundActionAll = (fundId, action) => {
    const target = !isFundActionAllChecked(fundId, action);
    setMatrix((prev) => {
      const fundM = { ...(prev[fundId] || {}) };
      modules.forEach((m) => {
        const mk = modKey(m);
        const cell = { ...(fundM[mk] || {}), [action]: target };
        // TY-42: apply same view-auto-enable / view-lock rules across the row.
        if (["can_add", "can_edit", "can_delete"].includes(action) && target) {
          cell.can_view = true;
        }
        if (action === "can_view" && !target && (cell.can_add || cell.can_edit || cell.can_delete)) {
          cell.can_view = true;
        }
        fundM[mk] = cell;
      });
      return { ...prev, [fundId]: fundM };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    // TY-46: collect ALL field-level errors in one pass and surface them
    // inline (red border + helper text) instead of firing sequential modals.
    // Server/network failures still surface via toast at the bottom.
    const nextErrors = { role_name: "", org_id: "", permissions: "" };
    if (!form.role_name.trim()) nextErrors.role_name = "Role name is required";
    if (!form.org_id) nextErrors.org_id = "Organization is required";

    let fundsPayload = [];
    let permissions = [];
    if (!auto) {
      fundsPayload = selectedFunds;
      fundsPayload.forEach((fid) => {
        const fundM = matrix[fid] || {};
        Object.entries(fundM).forEach(([mk, v]) => {
          if (v && (v.can_view || v.can_add || v.can_edit || v.can_delete)) {
            permissions.push({
              fund_id: fid,
              module_key: mk,
              can_view: !!v.can_view,
              can_add: !!v.can_add,
              can_edit: !!v.can_edit,
              can_delete: !!v.can_delete,
            });
          }
        });
      });
      if (fundsPayload.length > 0 && permissions.length === 0) {
        nextErrors.permissions = "Select at least one permission for the chosen funds";
      }
    }

    setErrors(nextErrors);
    const firstInvalid = ["role_name", "org_id", "permissions"].find((k) => nextErrors[k]);
    if (firstInvalid) {
      const target =
        firstInvalid === "role_name" ? nameNodeRef.current :
        firstInvalid === "org_id" ? orgAnchorNodeRef.current :
        permsAnchorNodeRef.current;
      if (target && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (firstInvalid === "role_name" && nameNodeRef.current && typeof nameNodeRef.current.focus === "function") {
        try { nameNodeRef.current.focus({ preventScroll: true }); } catch { nameNodeRef.current.focus(); }
      }
      return;
    }

    const payload = {
      role_name: form.role_name.trim(),
      // TY-36: backend expects role_description (not description).
      role_description: form.description || "",
      org_id: form.org_id,
      role_tag: roleTag,
      funds: fundsPayload,
      permissions,
    };

    await runSubmit(async (idempotencyKey) => {
      try {
        if (isEdit) {
          await API.put(`/roles-permissions/roles/${roleId}`, payload);
          toast.success("Role updated");
        } else {
          await API.post("/roles-permissions/roles", payload, {
            headers: { "Idempotency-Key": idempotencyKey },
          });
          toast.success("Role created");
        }
        navigate("/super-admin/roles-permissions");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Operation failed");
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center" data-testid="role-form-loading">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl" data-testid="role-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/super-admin/roles-permissions")} data-testid="role-form-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{isEdit ? "Edit Role" : "Create Role"}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Define the role, scope it to funds, and set per-module permissions.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="p-6 pb-4">
            <CardTitle className="font-heading flex items-center gap-2 text-lg">
              <ShieldCheck className="w-5 h-5 text-primary" /> Role Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role-name">Role Name *</Label>
                <Input
                  id="role-name"
                  ref={nameNodeRef}
                  value={form.role_name}
                  onChange={(e) => { setForm({ ...form, role_name: e.target.value }); if (errors.role_name) setErrors({ ...errors, role_name: "" }); }}
                  className={`bg-secondary/50 ${errors.role_name ? "border-red-500/60" : ""}`}
                  placeholder="Analyst"
                  aria-invalid={!!errors.role_name}
                  data-testid="role-name-input"
                />
                {errors.role_name && (
                  <p className="text-xs text-red-400" data-testid="role-name-error">{errors.role_name}</p>
                )}
                {!errors.role_name && form.role_name && (
                  <p className="text-[11px] text-muted-foreground">
                    Tag: <span className="font-mono">{roleTag || "—"}</span>
                  </p>
                )}
              </div>
              <div className="space-y-2" ref={orgAnchorNodeRef}>
                <Label>Organization *</Label>
                <Select
                  value={form.org_id}
                  onValueChange={(v) => { setForm({ ...form, org_id: v }); if (errors.org_id) setErrors({ ...errors, org_id: "" }); }}
                  disabled={isEdit}
                >
                  <SelectTrigger
                    className={`bg-secondary/50 ${errors.org_id ? "border-red-500/60" : ""}`}
                    aria-invalid={!!errors.org_id}
                    data-testid="role-org-select"
                  >
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {orgs.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.org_id && (
                  <p className="text-xs text-red-400" data-testid="role-org-error">{errors.org_id}</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-secondary/50"
                placeholder="Short description of what this role can do"
                rows={2}
                data-testid="role-description-input"
              />
            </div>
          </CardContent>
        </Card>

        {auto ? (
          <Card data-testid="role-auto-managed-notice">
            <CardContent className="p-6 flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium">Permissions managed automatically by GL</p>
                <p className="text-sm text-muted-foreground mt-1">
                  The <Badge variant="outline" className="text-[10px] mx-0.5">{roleTag}</Badge> role grants
                  access automatically — no funds or per-module permissions need to be configured here.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card ref={permsAnchorNodeRef}>
              <CardHeader className="p-6 pb-4">
                <CardTitle className="font-heading text-lg">Funds</CardTitle>
                <p className="text-muted-foreground text-sm">Select the funds this role applies to. Each fund gets its own permission matrix below.</p>
                {errors.permissions && (
                  <p className="text-xs text-red-400 mt-2" data-testid="role-permissions-error">{errors.permissions}</p>
                )}
                <div className="flex flex-col sm:flex-row gap-2 mt-3" data-testid="fund-picker-toolbar">
                  <input
                    type="text"
                    value={fundSearch}
                    onChange={(e) => setFundSearch(e.target.value)}
                    placeholder="Search funds..."
                    className="h-9 rounded-md border border-border bg-secondary/50 px-3 text-sm flex-1 min-w-[200px]"
                    data-testid="fund-picker-search"
                  />
                  <Select value={fundStatusFilter} onValueChange={setFundStatusFilter}>
                    <SelectTrigger className="h-9 w-full sm:w-40 bg-secondary/50 text-sm" data-testid="fund-picker-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                {fundsLoading ? (
                  <p className="text-sm text-muted-foreground">Loading funds…</p>
                ) : orgFunds.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No funds match the current filter.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="role-funds-list">
                    {orgFunds.map((f) => {
                      const status = (f.status || "").toLowerCase();
                      const isClosed = status === "closed";
                      const statusCls = STATUS_STYLES[status] || "bg-slate-500/10 text-slate-400 border-slate-500/20";
                      return (
                        <label
                          key={f.id}
                          className={`flex items-center gap-2 rounded-lg border border-border bg-secondary/30 px-3 py-2 transition-colors ${isClosed ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-secondary/50"}`}
                          data-testid={`role-fund-${f.id}`}
                          title={f.name}
                        >
                          <Checkbox
                            checked={selectedFunds.includes(f.id)}
                            onCheckedChange={() => { if (!isClosed) toggleFund(f.id); }}
                            disabled={isClosed}
                            data-testid={`role-fund-checkbox-${f.id}`}
                          />
                          <span className="text-sm truncate flex-1" title={f.name}>{f.name}</span>
                          <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${statusCls}`} data-testid={`role-fund-status-${f.id}`}>
                            {f.status || "—"}
                          </Badge>
                        </label>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {selectedFunds.length === 0 ? (
              <Card data-testid="role-no-funds-hint">
                <CardContent className="p-6 text-sm text-muted-foreground">
                  Select one or more funds above to configure their permissions.
                </CardContent>
              </Card>
            ) : (
              selectedFunds.map((fid) => {
                const fund = funds.find((f) => f.id === fid);
                return (
                  <Card key={fid} data-testid={`role-matrix-fund-${fid}`}>
                    <CardHeader className="p-6 pb-4">
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-primary" />
                        {fund?.name || fid}
                      </CardTitle>
                      <p className="text-muted-foreground text-sm">Set per-module permissions for this fund.</p>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                            <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold pl-6">Module</TableHead>
                            {ACTIONS.map((a) => (
                              <TableHead key={a.key} className="text-xs uppercase tracking-wider text-muted-foreground font-bold text-center">{a.label}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {modules.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-muted-foreground py-10">
                                No modules available.
                              </TableCell>
                            </TableRow>
                          ) : (
                            <>
                              <TableRow className="bg-secondary/20 hover:bg-secondary/20" data-testid={`role-selectall-row-${fid}`}>
                                <TableCell className="pl-6 font-semibold text-sm">Select All</TableCell>
                                {ACTIONS.map((a) => (
                                  <TableCell key={a.key} className="text-center">
                                    <Checkbox
                                      checked={isFundActionAllChecked(fid, a.key)}
                                      onCheckedChange={() => toggleFundActionAll(fid, a.key)}
                                      data-testid={`selectall-${fid}-${a.key}`}
                                    />
                                  </TableCell>
                                ))}
                              </TableRow>
                              {modules.map((m) => {
                                const mk = modKey(m);
                                const cell = matrix[fid]?.[mk] || {};
                                return (
                                  <TableRow key={mk} data-testid={`role-module-row-${fid}-${mk}`}>
                                    <TableCell className="pl-6 font-medium text-sm">{modLabel(m)}</TableCell>
                                    {ACTIONS.map((a) => (
                                      <TableCell key={a.key} className="text-center">
                                        <Checkbox
                                          checked={!!cell[a.key]}
                                          onCheckedChange={() => toggleAction(fid, mk, a.key)}
                                          data-testid={`perm-${fid}-${mk}-${a.key}`}
                                        />
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                );
                              })}
                            </>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </>
        )}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={() => navigate("/super-admin/roles-permissions")} data-testid="role-cancel-button">
            Cancel
          </Button>
          <Button type="submit" disabled={submitting} data-testid="role-submit-button">
            {submitting ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Update Role" : "Create Role")}
          </Button>
        </div>
      </form>
    </div>
  );
}
